import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import apiClient from '../config/axios';
import ModalConfirm from './ModalConfirm';
import ModalResult from './ModalResult';

const ModalWall = ({ open, onClose, onSuccess, editData = null, isEdit = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    officialGrade: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState({ message: '', severity: 'info' });

  // Popola il form quando è in modalità edit
  useEffect(() => {
    if (isEdit && editData) {
      setFormData({
        name: editData.name || '',
        officialGrade: editData.officialGrade || '',
        notes: editData.notes || ''
      });
    } else {
      setFormData({
        name: '',
        officialGrade: '',
        notes: ''
      });
    }
  }, [isEdit, editData, open]);

  // Gradi disponibili da 4a a 9a
  const availableGrades = [
    '4a', '4a+', '4b', '4b+', '4c', '4c+',
    '5a', '5a+', '5b', '5b+', '5c', '5c+',
    '6a', '6a+', '6b', '6b+', '6c', '6c+',
    '7a', '7a+', '7b', '7b+', '7c', '7c+',
    '8a', '8a+', '8b', '8b+', '8c', '8c+',
    '9a'
  ];

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      return;
    }

    setShowConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    try {
      setLoading(true);
      setShowConfirm(false);
      
      if (isEdit) {
        // Update existing wall
        await apiClient.put(`/api/walls/${editData._id}`, formData);
        setResultData({
          message: 'Via aggiornata con successo!',
          severity: 'success'
        });
      } else {
        // Create new wall
        await apiClient.post('/api/walls', formData);
        setResultData({
          message: 'Via creata con successo!',
          severity: 'success'
        });
      }
      
      setShowResult(true);
      
      // Reset form
      setFormData({
        name: '',
        officialGrade: '',
        notes: ''
      });
      
    } catch (err) {
      console.error('Errore nell\'operazione della via:', err);
      
      let errorMessage = isEdit ? 'Errore nell\'aggiornamento della via' : 'Errore nella creazione della via';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      setResultData({
        message: errorMessage,
        severity: 'error'
      });
      setShowResult(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
  };

  const handleResultClose = () => {
    setShowResult(false);
    // Chiudi sempre il modal principale dopo aver mostrato il risultato
    onClose();
    onSuccess?.();
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        officialGrade: '',
        notes: ''
      });
      setShowConfirm(false);
      setShowResult(false);
      setResultData({ message: '', severity: 'info' });
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon color="primary" />
          {isEdit ? 'Modifica Via' : 'Aggiungi Via'}
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {isEdit && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Importante:</strong> Se stai modificando questa via perché è una nuova via, allora eliminala e creane una nuova.
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Nome Via"
              value={formData.name}
              onChange={handleChange('name')}
              placeholder="Inserisci il nome della via..."
              required
              inputProps={{ maxLength: 100 }}
              helperText={`${formData.name.length}/100 caratteri`}
            />

            <FormControl fullWidth>
              <InputLabel>Grado Ufficiale</InputLabel>
              <Select
                value={formData.officialGrade}
                onChange={handleChange('officialGrade')}
                label="Grado Ufficiale"
              >
                <MenuItem value="">
                  <em>Nessun grado ufficiale</em>
                </MenuItem>
                {availableGrades.map((grade) => (
                  <MenuItem key={grade} value={grade}>
                    {grade}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Note (opzionale)"
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange('notes')}
              placeholder="Aggiungi note sulla via..."
              inputProps={{ maxLength: 500 }}
              helperText={`${formData.notes.length}/500 caratteri`}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            color="inherit"
          >
            Annulla
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !formData.name.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {loading ? (isEdit ? 'Aggiornando...' : 'Creando...') : (isEdit ? 'Aggiorna Via' : 'Crea Via')}
          </Button>
        </DialogActions>
      </form>

      <ModalConfirm
        open={showConfirm}
        title={isEdit ? "Conferma Modifica Via" : "Conferma Creazione Via"}
        message={`Sei sicuro di voler ${isEdit ? 'aggiornare' : 'creare'} la via "${formData.name}"${formData.officialGrade ? ` con grado ${formData.officialGrade}` : ''}?${formData.notes ? `\n\nNote: "${formData.notes}"` : ''}`}
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelConfirm}
      />

      <ModalResult
        open={showResult}
        onClose={handleResultClose}
        message={resultData.message}
        severity={resultData.severity}
        title={resultData.severity === 'success' ? 'Successo' : 'Errore'}
      />
    </Dialog>
  );
};

export default ModalWall;
